import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground",
        secondary:
          "border border-blue-200 bg-blue-50 text-blue-700",
        destructive:
          "border-transparent bg-red-600 text-white",
        outline: "text-foreground",
        success:
          "border-transparent bg-green-500 text-white",
        warning:
          "border-transparent bg-yellow-500 text-white",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

/**
 * A span, not a div.
 *
 * Badges sit inside running text -- a page subtitle, a card description, a form hint --
 * and every one of those slots renders a <p> (AdminPageHeader's description,
 * CardDescription, FormDescription, Radix's Dialog/Sheet descriptions). A <p> accepts
 * only phrasing content, so a <div> badge inside one makes the browser close the
 * paragraph early and the server and client trees diverge: "In HTML, <div> cannot be a
 * descendant of <p>. This will cause a hydration error."
 *
 * Purely an element change. The base class is `inline-flex`, which overrides the default
 * display of either element, so this renders pixel-identically across all ~364 call
 * sites -- and none of them puts block-level content inside a badge.
 */
function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <span className={cn(badgeVariants({ variant }), className)} {...props} />
  )
}

export { Badge, badgeVariants }
